import { useAuthStore } from '@/stores/auth';
import type {
    Account,
    AccountLifecycle,
    AccountStatus,
    BillingInterval,
    Entitlements,
    Feature,
    Limit,
    Plan,
} from '@/types';
import { computed, type ComputedRef } from 'vue';

/**
 * Plan entitlements composable (MILO-181 / MILO-97).
 *
 * Reads the `account` block from the me-payload and answers the gating
 * questions the UI needs: which features are allowed, how much of each limit
 * is left, and what subscription state the account is in. Mirrors the backend
 * EntitlementsService — but remember: the frontend only hides/disables, the
 * backend enforces.
 *
 * Platform admins have no account (`account === null`); they are never gated,
 * so `allows()` is always true and the account is treated as active.
 */
export type UseEntitlementsReturn = {
    account: ComputedRef<Account | null>;
    entitlements: ComputedRef<Entitlements | null>;
    plan: ComputedRef<Plan | null>;
    status: ComputedRef<AccountStatus | null>;
    interval: ComputedRef<BillingInterval | null>;
    isOwner: ComputedRef<boolean>;
    isPlatformAdmin: ComputedRef<boolean>;
    onTrial: ComputedRef<boolean>;
    isActive: ComputedRef<boolean>;
    isLapsed: ComputedRef<boolean>;
    isReadOnly: ComputedRef<boolean>;
    isPastDue: ComputedRef<boolean>;
    lifecycle: ComputedRef<AccountLifecycle | null>;
    inTransition: ComputedRef<boolean>;
    inRetrieval: ComputedRef<boolean>;
    trialEndsAt: ComputedRef<string | null>;
    trialDaysRemaining: ComputedRef<number | null>;
    allows: (feature: Feature) => boolean;
    limit: (limit: Limit) => number | null;
    usage: (limit: Limit) => number;
    remaining: (limit: Limit) => number | null;
    hasReachedLimit: (limit: Limit) => boolean;
};

export function useEntitlements(): UseEntitlementsReturn {
    // Read store state directly (not via storeToRefs) so the composable stays
    // safe when consumers mock the auth store with a plain object — `auth.user`
    // then resolves to undefined instead of throwing on a missing ref.
    const auth = useAuthStore();

    const account = computed<Account | null>(() => auth.user?.account ?? null);
    const entitlements = computed<Entitlements | null>(() => account.value?.entitlements ?? null);
    const subscription = computed(() => account.value?.subscription ?? null);

    const plan = computed<Plan | null>(() => subscription.value?.plan ?? null);
    const status = computed<AccountStatus | null>(() => subscription.value?.status ?? null);
    const interval = computed<BillingInterval | null>(() => subscription.value?.interval ?? null);
    const isOwner = computed(() => account.value?.is_owner ?? false);
    // An authenticated user without an account is a platform admin — never gated.
    // Distinct from being logged out (no user at all), which grants nothing.
    const isPlatformAdmin = computed(() => !!auth.user && account.value === null);
    const onTrial = computed(() => subscription.value?.on_trial ?? false);
    const isLapsed = computed(() => status.value === 'lapsed');
    const isPastDue = computed(() => status.value === 'past_due');
    const isActive = computed(() => isPlatformAdmin.value || (!!account.value && !isLapsed.value));
    // A lapsed account is read-only: mutating actions are blocked (except the
    // billing self-service + own profile/2FA/tokens/PDF whitelist). Platform
    // admins are never gated.
    const isReadOnly = computed(() => isLapsed.value && !isPlatformAdmin.value);
    // The wind-down after the subscription ended (MILO-146). Optional-chained
    // because a me-payload from before the field existed carries no block.
    const lifecycle = computed<AccountLifecycle | null>(() => account.value?.lifecycle ?? null);
    const inTransition = computed(() => lifecycle.value?.phase === 'transition');
    const inRetrieval = computed(() => lifecycle.value?.phase === 'retrieval');
    const trialEndsAt = computed<string | null>(() => subscription.value?.trial_ends_at ?? null);
    const trialDaysRemaining = computed<number | null>(() => {
        if (!onTrial.value || !trialEndsAt.value) return null;
        const ms = new Date(trialEndsAt.value).getTime() - Date.now();
        return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    });

    /** Whether the account's plan grants a feature. Platform admins are ungated. */
    const allows = (feature: Feature): boolean =>
        isPlatformAdmin.value || (entitlements.value?.features.includes(feature) ?? false);

    /** Included quantity for a limit; `null` means unlimited (or unknown). */
    const limit = (l: Limit): number | null => entitlements.value?.limits[l] ?? null;

    /** Live usage for a limit. */
    const usage = (l: Limit): number => entitlements.value?.usage[l] ?? 0;

    /** How many of a limit are still available; `null` when unlimited. */
    const remaining = (l: Limit): number | null => {
        const max = limit(l);
        if (max === null) return null;
        return Math.max(0, max - usage(l));
    };

    /** Whether a limit has been fully consumed (never true for unlimited). */
    const hasReachedLimit = (l: Limit): boolean => {
        const max = limit(l);
        return max !== null && usage(l) >= max;
    };

    return {
        account,
        entitlements,
        plan,
        status,
        interval,
        isOwner,
        isPlatformAdmin,
        onTrial,
        isActive,
        isLapsed,
        isReadOnly,
        isPastDue,
        lifecycle,
        inTransition,
        inRetrieval,
        trialEndsAt,
        trialDaysRemaining,
        allows,
        limit,
        usage,
        remaining,
        hasReachedLimit,
    };
}
